import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { InputNumber } from "primereact/inputnumber";
import { ProgressSpinner } from "primereact/progressspinner";
import { getSessions } from "../../api/sessionApi";
import { getGrades } from "../../api/gradeApi";
import {
  getVouchers,
  createVouchers,
  saveOrUpdateVouchers,
  getTermVouchers,
  createTermVouchers,
} from "../../api/feeVoucherApi";
import { getTerms } from "../../api/termApi";
import { getCashBookSetup } from "../../api/accountingApi";
import { useCurrency, formatMoney } from "../../common/utils/currency";
import { useSchoolFeatures } from "../../common/utils/schoolSettings";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const STATUS_OPTIONS = [
  { label: "Unpaid", value: "unpaid" },
  { label: "Paid", value: "paid" },
  { label: "Overdue", value: "overdue" },
  { label: "Cancelled", value: "cancelled" },
];

const FeeVoucherPage = () => {
  const toast = useRef(null);
  const currency = useCurrency();
  // When the school bills weekly-per-term, the Month selector becomes a Term
  // selector and totals are driven by the term's available week count.
  const { weeklyBilling } = useSchoolFeatures();
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [currentAvailableWeeks, setCurrentAvailableWeeks] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [grades, setGrades] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedRows, setEditedRows] = useState({});
  // Where paid fees are deposited in accounting (default Cash in Hand).
  // Empty list = user lacks Accounting access; the dropdown stays hidden and
  // the server falls back to Cash in Hand.
  const [depositAccounts, setDepositAccounts] = useState([]);
  const [depositAccountId, setDepositAccountId] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [sessRes, gradeRes] = await Promise.all([getSessions(), getGrades()]);
      const sessionList = sessRes.data || sessRes || [];
      const gradeList = gradeRes.data || gradeRes || [];
      setSessions(sessionList);
      setGrades(gradeList);

      // Auto-select active session
      const active = sessionList.find(s => s.is_active);
      if (active) setSelectedSession(active.id);

      try {
        const setup = await getCashBookSetup();
        setDepositAccounts(setup.cashAccounts || []);
        setDepositAccountId(setup.defaultCashAccountId || null);
      } catch {
        // No Accounting permission — server defaults to Cash in Hand.
      }

      // Auto-select current month
      setSelectedMonth(MONTHS[new Date().getMonth()]);
    } catch (err) {
      console.error("Error loading initial data:", err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to load sessions/grades" });
    }
  };

  // Reload the term list whenever the session changes (weekly billing only)
  useEffect(() => {
    if (!weeklyBilling || !selectedSession) return;
    let cancelled = false;
    getTerms(selectedSession)
      .then((res) => {
        if (cancelled) return;
        const list = res?.data || [];
        setTerms(list);
        setSelectedTerm((prev) => (list.some((t) => t.id === prev) ? prev : list[0]?.id ?? null));
      })
      .catch(() => {
        if (!cancelled) setTerms([]);
      });
    return () => {
      cancelled = true;
    };
  }, [weeklyBilling, selectedSession]);

  const loadVouchers = async () => {
    if (weeklyBilling) return loadTermVouchers();
    if (!selectedSession || !selectedMonth) {
      toast.current?.show({ severity: "warn", summary: "Required", detail: "Select session and month" });
      return;
    }
    setLoading(true);
    try {
      const gradeIds = selectedGrade || "all";
      const data = await getVouchers(selectedSession, selectedMonth, gradeIds);
      setVouchers(Array.isArray(data) ? data : []);
      setEditedRows({});
    } catch (err) {
      console.error("Error loading vouchers:", err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to load vouchers" });
    } finally {
      setLoading(false);
    }
  };

  const loadTermVouchers = async () => {
    if (!selectedTerm) {
      toast.current?.show({ severity: "warn", summary: "Required", detail: "Select a term" });
      return;
    }
    setLoading(true);
    try {
      const data = await getTermVouchers(selectedTerm, selectedGrade || "all");
      setVouchers(data?.vouchers || []);
      setCurrentAvailableWeeks(data?.currentAvailableWeeks ?? null);
      setEditedRows({});
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.response?.data?.message || "Failed to load term vouchers",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTerm = async (regenerate = false) => {
    if (!selectedTerm) {
      toast.current?.show({ severity: "warn", summary: "Required", detail: "Select a term" });
      return;
    }
    setGenerating(true);
    try {
      const result = await createTermVouchers({
        termId: selectedTerm,
        gradeIds: selectedGrade || "all",
        regenerate,
      });
      const skipped = result.skippedPaid
        ? `, skipped ${result.skippedPaid} paid`
        : "";
      toast.current?.show({
        severity: "success",
        summary: `${result.availableWeeks} week(s) billed`,
        detail: `Created: ${result.created}, Updated: ${result.updated}${skipped}`,
      });
      await loadTermVouchers();
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.response?.data?.message || "Failed to generate term vouchers",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerate = async () => {
    if (weeklyBilling) return handleGenerateTerm(false);
    if (!selectedSession || !selectedMonth) {
      toast.current?.show({ severity: "warn", summary: "Required", detail: "Select session and month" });
      return;
    }
    setGenerating(true);
    try {
      const gradeIds = selectedGrade || "all";
      const result = await createVouchers({ sessionId: selectedSession, gradeIds, month: selectedMonth });
      toast.current?.show({
        severity: "success",
        summary: "Generated",
        detail: `Created: ${result.created}, Updated: ${result.updated}`
      });
      await loadVouchers();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to generate vouchers";
      toast.current?.show({ severity: "error", summary: "Error", detail: msg });
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    const edited = Object.values(editedRows);
    if (edited.length === 0) {
      toast.current?.show({ severity: "info", summary: "No Changes", detail: "No vouchers were modified" });
      return;
    }
    setSaving(true);
    try {
      const voucherData = edited.map(v => ({
        // Term vouchers have no month, so the server matches on id instead.
        voucherId: v.id,
        studentId: v.student_id,
        gradeId: v.grade_id,
        base_fee: v.base_fee,
        transport_fee: v.transport_fee,
        discount: v.discount,
        fine_amount: v.fine_amount || 0,
        status: v.status,
        due_date: v.due_date,
        remarks: v.remarks,
        payment_account_id: depositAccountId,
      }));
      const saveRes = await saveOrUpdateVouchers({
        sessionId: selectedSession,
        month: weeklyBilling ? null : selectedMonth,
        voucherData,
      });
      (saveRes?.warnings || []).forEach((w) =>
        toast.current?.show({ severity: "warn", summary: "Accounting", detail: w, life: 8000 })
      );
      toast.current?.show({ severity: "success", summary: "Saved", detail: `${edited.length} voucher(s) updated` });
      setEditedRows({});
      await loadVouchers();
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to save vouchers" });
    } finally {
      setSaving(false);
    }
  };

  const onCellEdit = (rowData, field, value) => {
    const updated = { ...rowData, [field]: value };
    updated.total_fee = (updated.base_fee || 0) + (updated.transport_fee || 0) + (updated.fine_amount || 0) - (updated.discount || 0);

    // Update in vouchers array for display
    setVouchers(prev => prev.map(v => v.id === rowData.id ? { ...v, ...updated } : v));
    // Track as edited
    setEditedRows(prev => ({ ...prev, [rowData.id]: updated }));
  };

  const statusTemplate = (rowData) => {
    const colors = { paid: "success", unpaid: "warning", overdue: "danger", cancelled: "info" };
    return <Tag value={rowData.status} severity={colors[rowData.status] || "warning"} />;
  };

  const currencyTemplate = (rowData, field) => {
    const val = rowData[field.field];
    return formatMoney(val != null ? parseFloat(val) : 0, currency);
  };

  const editableCurrencyTemplate = (rowData, field) => (
    <InputNumber
      value={rowData[field.field] || 0}
      onValueChange={(e) => onCellEdit(rowData, field.field, e.value)}
      mode="decimal"
      minFractionDigits={0}
      maxFractionDigits={2}
      className="w-full p-inputtext-sm"
    />
  );

  const statusEditor = (rowData) => (
    <Dropdown
      value={rowData.status}
      options={STATUS_OPTIONS}
      onChange={(e) => onCellEdit(rowData, "status", e.value)}
      className="w-full p-inputtext-sm"
    />
  );

  const sessionOptions = sessions.map(s => ({ label: s.name, value: s.id }));
  const gradeOptions = [
    { label: "All Grades", value: null },
    ...grades.map(g => ({ label: g.name, value: g.id }))
  ];
  const monthOptions = MONTHS.map(m => ({ label: m, value: m }));
  const termOptions = terms.map(t => ({
    label: `${t.name} — ${t.available_weeks}/${t.total_weeks} weeks`,
    value: t.id,
  }));
  const activeTerm = terms.find(t => t.id === selectedTerm);
  const staleVouchers =
    weeklyBilling && currentAvailableWeeks != null
      ? vouchers.filter(
          (v) => v.weeks_billed != null && v.weeks_billed !== currentAvailableWeeks
        ).length
      : 0;

  const leftToolbar = () => (
    <div className="flex flex-wrap gap-2 align-items-center">
      <Dropdown
        value={selectedSession}
        options={sessionOptions}
        onChange={(e) => setSelectedSession(e.value)}
        placeholder="Session"
        className="w-12rem"
      />
      {weeklyBilling ? (
        <Dropdown
          value={selectedTerm}
          options={termOptions}
          onChange={(e) => setSelectedTerm(e.value)}
          placeholder="Term"
          className="w-14rem"
          emptyMessage="No terms in this session"
        />
      ) : (
        <Dropdown
          value={selectedMonth}
          options={monthOptions}
          onChange={(e) => setSelectedMonth(e.value)}
          placeholder="Month"
          className="w-10rem"
        />
      )}
      <Dropdown
        value={selectedGrade}
        options={gradeOptions}
        onChange={(e) => setSelectedGrade(e.value)}
        placeholder="Grade"
        className="w-10rem"
      />
      <Button
        label="Load"
        icon="pi pi-search"
        onClick={loadVouchers}
        className="p-button-sm"
      />
    </div>
  );

  const rightToolbar = () => (
    <div className="flex gap-2 align-items-center">
      {depositAccounts.length > 0 && (
        <Dropdown
          value={depositAccountId}
          options={depositAccounts.map((a) => ({ label: a.name, value: a.id }))}
          onChange={(e) => setDepositAccountId(e.value)}
          placeholder="Deposit to"
          className="p-inputtext-sm"
          tooltip="Account where paid fees are recorded in accounting"
        />
      )}
      <Button
        label="Generate"
        icon="pi pi-plus"
        onClick={handleGenerate}
        loading={generating}
        className="p-button-sm p-button-success"
        tooltip={
          weeklyBilling
            ? "Create vouchers for students without one in this term"
            : "Generate vouchers for selected grade/month"
        }
      />
      {weeklyBilling && (
        <Button
          label="Recalculate"
          icon="pi pi-refresh"
          onClick={() => handleGenerateTerm(true)}
          loading={generating}
          className="p-button-sm p-button-warning"
          tooltip="Re-bill unpaid vouchers using the current available week count"
        />
      )}
      <Button
        label="Save Changes"
        icon="pi pi-save"
        onClick={handleSave}
        loading={saving}
        disabled={Object.keys(editedRows).length === 0}
        className="p-button-sm"
      />
    </div>
  );

  if (loading && vouchers.length === 0) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ height: "60vh" }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="mb-3">
        <h2 className="m-0">Fee Vouchers</h2>
        <p className="text-500 mt-1 mb-0">
          {weeklyBilling
            ? "Weekly billing — each voucher covers the available weeks in a term"
            : "Generate and manage student fee vouchers"}
        </p>
      </div>

      <Toolbar className="mb-3" left={leftToolbar} right={rightToolbar} />

      {weeklyBilling && activeTerm && (
        <div className="mb-3 p-3 surface-ground border-round flex flex-wrap gap-3 align-items-center">
          <span>
            <strong>{activeTerm.name}</strong>
          </span>
          <span className="text-500">
            {activeTerm.available_weeks} of {activeTerm.total_weeks} weeks available
          </span>
          {activeTerm.weekly_fee != null && (
            <span className="text-500">
              Default rate {formatMoney(activeTerm.weekly_fee, currency)} / week
            </span>
          )}
          {/* A voucher billed before the admin toggled weeks needs re-issuing. */}
          {staleVouchers > 0 && (
            <span className="text-orange-500">
              <i className="pi pi-exclamation-triangle mr-1" />
              {staleVouchers} voucher(s) were billed for a different number of weeks — use
              Recalculate to re-issue the unpaid ones.
            </span>
          )}
        </div>
      )}

      <div className="card">
        <DataTable
          value={vouchers}
          paginator
          rows={20}
          rowsPerPageOptions={[10, 20, 50]}
          emptyMessage="No vouchers found. Select filters and click Load, or Generate new vouchers."
          className="p-datatable-sm"
          loading={loading}
          sortField="student.full_name"
          sortOrder={1}
          scrollable
          scrollHeight="calc(100vh - 320px)"
        >
          <Column
            header="Student"
            body={(row) => row.student?.full_name || "—"}
            sortable
            sortField="student.full_name"
            style={{ minWidth: "12rem" }}
          />
          <Column
            header="Grade"
            body={(row) => row.grade?.name || "—"}
            sortable
            sortField="grade.name"
            style={{ minWidth: "8rem" }}
          />
          {weeklyBilling && (
            <Column
              header="Weeks"
              body={(row) => (
                <Tag
                  value={row.weeks_billed ?? "—"}
                  severity={
                    row.weeks_billed != null &&
                    currentAvailableWeeks != null &&
                    row.weeks_billed !== currentAvailableWeeks
                      ? "warning"
                      : "info"
                  }
                />
              )}
              style={{ minWidth: "6rem" }}
            />
          )}
          {weeklyBilling && (
            <Column
              header="Rate/wk"
              body={(row) =>
                row.weekly_rate != null ? formatMoney(row.weekly_rate, currency) : "—"
              }
              style={{ minWidth: "7rem" }}
            />
          )}
          <Column
            field="base_fee"
            header={weeklyBilling ? "Tuition" : "Base Fee"}
            body={editableCurrencyTemplate}
            style={{ minWidth: "8rem" }}
          />
          <Column field="transport_fee" header="Transport" body={editableCurrencyTemplate} style={{ minWidth: "8rem" }} />
          <Column field="discount" header="Discount" body={editableCurrencyTemplate} style={{ minWidth: "8rem" }} />
          <Column field="fine_amount" header="Fine" body={editableCurrencyTemplate} style={{ minWidth: "7rem" }} />
          <Column field="total_fee" header="Total" body={currencyTemplate} style={{ minWidth: "8rem", fontWeight: "bold" }} />
          <Column field="status" header="Status" body={statusTemplate} editor={statusEditor} style={{ minWidth: "8rem" }} />
          <Column
            header="Actions"
            body={(row) => (
              <Dropdown
                value={row.status}
                options={STATUS_OPTIONS}
                onChange={(e) => onCellEdit(row, "status", e.value)}
                className="p-inputtext-sm"
                style={{ width: "8rem" }}
              />
            )}
            style={{ minWidth: "9rem" }}
          />
        </DataTable>
      </div>

      {Object.keys(editedRows).length > 0 && (
        <div className="mt-2 text-500 text-sm">
          <i className="pi pi-info-circle mr-1" />
          {Object.keys(editedRows).length} voucher(s) modified. Click "Save Changes" to persist.
        </div>
      )}

      <Toast ref={toast} />
    </div>
  );
};

export default FeeVoucherPage;
